// Home view container (T-042..T-045, plan §3 / §4).
//
// Wires the gradient hero plus the four list cards from spec §5–§8
// into a single page driven by GET /api/home (shipped in T-041).
//
// Step 6 (T-045) adds the page-level `<LocationFilter>` per spec §9:
//   - URL state — `?location=<siteId>` is the source of truth (deep
//     links work; back/forward restore the filter).
//   - localStorage `home.locationFilter` is the per-user default —
//     consulted on first mount when no URL param is present, written
//     on every change so reload remembers the choice.
//   - Birthdays bypass the filter (spec §9 explicitly excludes DOB
//     from location scoping); the server already wires this up.
//
// Final outer chrome (`bg-[#F8F9FB]` page background, max-width spacing)
// is part of step 7 pixel polish (T-046).

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { HomeResponse } from '@pa/shared';
import { api } from '../api/client';
import { HeroCard } from './Home/HeroCard';
import { AvailabilityCard } from './Home/AvailabilityCard';
import { NewHiresCard } from './Home/NewHiresCard';
import { AnniversariesCard } from './Home/AnniversariesCard';
import { BirthdaysCard } from './Home/BirthdaysCard';
import { LocationFilter } from './Home/LocationFilter';

const LOCATION_STORAGE_KEY = 'home.locationFilter';

export default function HomeView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlLocation = searchParams.get('location');

  // Hydrate on first render: if the URL has no `?location` param,
  // inherit the user's last-used filter from localStorage. Doing this
  // inside an effect (rather than as initial state) means the URL is
  // always the source of truth on subsequent navigations — no stale
  // localStorage shadowing a fresh deep link.
  useEffect(() => {
    if (urlLocation !== null) return;
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(LOCATION_STORAGE_KEY);
    } catch {
      // SSR / restricted storage — ignore.
    }
    if (stored && stored !== 'all') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('location', stored as string);
          return next;
        },
        { replace: true },
      );
    }
    // We only want this on the very first mount; later URL changes
    // are intentional (the user clicked an option). Listing
    // dependencies would re-run the hydrate when we don't want to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve the active location id for the API + the filter trigger.
  // `?location=all` and missing-param are equivalent → null.
  const activeLocationId = useMemo(
    () => (urlLocation && urlLocation !== 'all' ? urlLocation : null),
    [urlLocation],
  );

  const home = useQuery({
    queryKey: ['home', activeLocationId ?? 'all'],
    queryFn: () =>
      api.get<HomeResponse>(
        `/api/home${activeLocationId ? `?location=${encodeURIComponent(activeLocationId)}` : ''}`,
      ),
    staleTime: 5 * 60_000, // plan §4.1
  });

  const onLocationChange = useCallback(
    (next: string | null) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next === null) out.delete('location');
          else out.set('location', next);
          return out;
        },
        { replace: false },
      );
      try {
        if (next === null) {
          window.localStorage.removeItem(LOCATION_STORAGE_KEY);
        } else {
          window.localStorage.setItem(LOCATION_STORAGE_KEY, next);
        }
      } catch {
        // Ignore storage errors — URL is the canonical state.
      }
    },
    [setSearchParams],
  );

  const availableLocations = home.data?.filters.availableLocations ?? [];

  // Spec §3 wants the *whole* page on a `bg-[#F8F9FB]` outer container.
  // The AppShell's `<main>` content slot is already a `p-8` div, so we
  // bleed out with negative margin (`-m-8`) to fill that whole area
  // edge-to-edge, then re-apply the padding inside the coloured
  // wrapper. Net DOM movement is zero pixels — this is purely a
  // background swap relative to T-045's white default.
  return (
    <div className="-m-8 bg-[#F8F9FB] min-h-[calc(100vh-5rem)] p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex justify-end">
          <LocationFilter
            options={availableLocations}
            activeLocationId={activeLocationId}
            onChange={onLocationChange}
          />
        </div>
        {home.isLoading && <HeroSkeleton />}
        {home.isError && (
          <div className="bg-white rounded-2xl border border-rose-100 p-6 text-sm text-rose-700">
            Couldn't load your home view. Try refreshing the page.
          </div>
        )}
        {home.data && (
          <>
            <HeroCard hero={home.data.hero} />
            <AvailabilityCard rows={home.data.availabilityToday} />
            <NewHiresCard hires={home.data.newHires} />
            <AnniversariesCard anniversaries={home.data.anniversaries} />
            <BirthdaysCard birthdays={home.data.birthdays} />
          </>
        )}
      </div>
    </div>
  );
}

function HeroSkeleton() {
  return (
    <div
      data-testid="home-hero-skeleton"
      className="rounded-2xl shadow-lg h-56 animate-pulse"
      style={{
        background:
          'linear-gradient(135deg, #6344C6 0%, #8B6DE8 50%, #A78BFA 100%)',
      }}
    />
  );
}
